import { createSelector } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import type { BuildingUnitTenancy } from 'shared';
import { RootState } from 'store/store';
import type { BuildingUnit } from './types';
import { isBuildingUnitTenancyActiveOnDate } from 'ux/Tenant/CleaningService/Onboarding/utils';

const normalizeString = (value?: string | null) => {
  if (value === undefined || value === null) {
    return '';
  }

  return `${value}`.trim().toLowerCase();
};

const tenancyMatchesUnit = (tenancy: BuildingUnitTenancy, unit: BuildingUnit) => {
  const unitName = normalizeString(unit.name);
  const tenancyUnitName = normalizeString(tenancy.building_unit_name);

  const unitLine1 = normalizeString(unit.address?.line1);
  const unitLine2 = normalizeString(unit.address?.line2);

  const tenancyLine1 = normalizeString(tenancy.address?.line1 ?? tenancy.building_line1);
  const tenancyLine2 = normalizeString(tenancy.address?.line2);

  if (tenancyLine1 && unitLine1 && tenancyLine1 === unitLine1) {
    if (tenancyLine2 && unitLine2) {
      return tenancyLine2 === unitLine2;
    }

    if (tenancyUnitName && unitName) {
      return tenancyUnitName === unitName;
    }

    return true;
  }

  if (tenancyUnitName && unitName && tenancyUnitName === unitName) {
    if (tenancyLine1 && unitLine1) {
      return tenancyLine1 === unitLine1;
    }

    return true;
  }

  const tenancyBuildingAddress = normalizeString(tenancy.building_address);
  if (tenancyBuildingAddress && unitLine1) {
    const composedUnitAddress = unit.name ? normalizeString(`${unit.address?.line1 ?? ''}, ${unit.name}`) : unitLine1;

    if (tenancyBuildingAddress === composedUnitAddress) {
      return true;
    }
  }

  return false;
};

const getTenanciesForUnit = (
  unit: BuildingUnit,
  buildingUnitTenancies?: BuildingUnitTenancy[] | null
): BuildingUnitTenancy[] | undefined => {
  if (unit.building_unit_tenancies?.length) {
    return unit.building_unit_tenancies;
  }

  if (!buildingUnitTenancies?.length) {
    return undefined;
  }

  const matchingTenancies = buildingUnitTenancies.filter(tenancy => tenancyMatchesUnit(tenancy, unit));

  return matchingTenancies.length ? matchingTenancies : undefined;
};

export const selectTenancy = (state: RootState) => state.Tenancy;

export const selectServicesPaymentMethod = createSelector(selectTenancy, tenancy => tenancy.services_payment_method);

export const selectRentPaymentMethod = createSelector(selectTenancy, tenancy => tenancy.rent_payment_method);

export const selectBillingContacts = createSelector(selectTenancy, tenancy => tenancy.billing_contacts);

export const selectBuildingUnits = createSelector(selectTenancy, tenancy => tenancy.building_units);

export const selectBuildingUnitTenancies = createSelector(selectTenancy, tenancy => tenancy.building_unit_tenancies);

export const selectActiveOrUpcomingBuildingUnit = (buildingId: number) =>
  createSelector([selectBuildingUnits, selectBuildingUnitTenancies], (buildingUnits, buildingUnitTenancies) => {
    if (!buildingUnits?.length) return undefined;

    const unitsForBuilding = buildingUnits.filter(unit => unit.building_id === buildingId);
    if (!unitsForBuilding.length) return undefined;

    const now = DateTime.now();

    const activeUnit = unitsForBuilding.find(unit => {
      const tenanciesForUnit = getTenanciesForUnit(unit, buildingUnitTenancies);
      return tenanciesForUnit?.some(tenancy => isBuildingUnitTenancyActiveOnDate(tenancy, now));
    });
    if (activeUnit) return activeUnit;

    const upcomingUnit = unitsForBuilding.find(unit => {
      const tenanciesForUnit = getTenanciesForUnit(unit, buildingUnitTenancies);
      return tenanciesForUnit?.some(tenancy => {
        if (!tenancy.start_date) return false;
        const startDate = DateTime.fromISO(tenancy.start_date, { zone: 'utc' });
        if (!startDate.isValid) return false;
        return startDate.startOf('day') >= now.startOf('day');
      });
    });

    return upcomingUnit;
  });

export const selectMembers = createSelector(selectTenancy, tenancy => tenancy.members);

export const selectPayments = createSelector(selectTenancy, tenancy => tenancy.payments);

export const selectFailedPayments = createSelector(selectTenancy, tenancy => tenancy.failed_payments);

export const selectFailedPlatformFeePayment = createSelector(selectPayments, payments =>
  payments?.find(
    payment => payment.status === 'Failed' && payment.payment_items?.some(item => item.type === 'PlatformFee')
  )
);

export const selectPlatformFees = createSelector(selectTenancy, tenancy => tenancy.platform_fees);
export const selectProposedPlatformFee = createSelector(selectPlatformFees, platformFees =>
  platformFees?.find(fee => !!fee.sent_at && !fee.approved_at)
);
export const selectActivePlatformFee = createSelector(selectPlatformFees, platformFees => {
  const approvedFees = platformFees?.filter(
    fee =>
      !!fee.approved_at &&
      (!fee.end_date || DateTime.fromISO(fee.end_date).startOf('day') >= DateTime.now().startOf('day'))
  );

  if (!approvedFees?.length) return undefined;

  // If there are multiple active fees, prefer the one with a future end date
  const feesWithEndDate = approvedFees.filter(fee => fee.end_date);
  if (feesWithEndDate.length) {
    return feesWithEndDate.sort(
      (a, b) => DateTime.fromISO(b.end_date!).valueOf() - DateTime.fromISO(a.end_date!).valueOf()
    )[0];
  }

  // Otherwise return the first active fee
  return approvedFees[0];
});
export const selectExpiredPlatformFee = createSelector(
  selectPlatformFees,
  platformFees =>
    platformFees
      ?.filter(fee => !!fee.end_date && DateTime.fromISO(fee.end_date!).startOf('day') < DateTime.now().startOf('day'))
      .sort((a, b) => DateTime.fromISO(b.end_date!).valueOf() - DateTime.fromISO(a.end_date!).valueOf())[0]
);
export const selectUpcomingPlatformFee = createSelector(selectPlatformFees, platformFees =>
  platformFees?.find(
    fee =>
      !!fee.approved_at &&
      !!fee.start_date &&
      DateTime.fromISO(fee.start_date).startOf('day') > DateTime.now().startOf('day')
  )
);

export const selectPaymentById = (id: number) =>
  createSelector(selectTenancy, tenancy => tenancy.payments.find(payment => payment.id === id));

export const selectSalesTaxRate = createSelector(selectTenancy, tenancy => tenancy.sales_tax_rate);

export const selectBuildingUnitTenanciesWithRent = (state: RootState) =>
  state.Tenancy.building_unit_tenancies?.filter(building_unit_tenancy => building_unit_tenancy.rent_available);

export const selectActiveBuildingUnitTenancies = createSelector(selectBuildingUnitTenancies, buildingUnitTenancies =>
  buildingUnitTenancies?.filter(tenancy => isBuildingUnitTenancyActiveOnDate(tenancy, DateTime.now()))
);
export const selectActiveOrUpcomingBuildingUnitTenancy = (buildingUnitId: number) =>
  createSelector([selectBuildingUnits, selectBuildingUnitTenancies], (buildingUnits, buildingUnitTenancies) => {
    if (!buildingUnits?.length) return undefined;

    const buildingUnit = buildingUnits.find(unit => unit.id === buildingUnitId);
    if (!buildingUnit) return undefined;

    const tenanciesForUnit = getTenanciesForUnit(buildingUnit, buildingUnitTenancies);
    if (!tenanciesForUnit?.length) return undefined;

    const now = DateTime.now();

    const activeTenancy = tenanciesForUnit.find(tenancy => isBuildingUnitTenancyActiveOnDate(tenancy, now));
    if (activeTenancy) return activeTenancy;

    const upcomingTenancy = tenanciesForUnit
      .filter(tenancy => {
        if (!tenancy.start_date) return false;
        const startDate = DateTime.fromISO(tenancy.start_date, { zone: 'utc' });
        if (!startDate.isValid) return false;
        return startDate.startOf('day') >= now.startOf('day');
      })
      .sort((a, b) => DateTime.fromISO(a.start_date).valueOf() - DateTime.fromISO(b.start_date).valueOf())[0];

    return upcomingTenancy;
  });

export const selectFirstUpcomingTenancyFromNow = createSelector(selectBuildingUnitTenancies, buildingUnitTenancies => {
  const now = DateTime.now().startOf('day');

  return buildingUnitTenancies
    ?.filter(tenancy => DateTime.fromISO(tenancy.start_date).startOf('day') >= now.startOf('day'))
    .sort((a, b) => DateTime.fromISO(a.start_date).diff(DateTime.fromISO(b.start_date)).toMillis())[0];
});

export const selectRentTotal = createSelector(selectBuildingUnitTenanciesWithRent, buildingUnitTenancies =>
  buildingUnitTenancies?.reduce(
    (acc: number, buildingUnitTenancy: BuildingUnitTenancy) =>
      acc + (buildingUnitTenancy.monthly_rent_amount_cents || 0),
    0
  )
);

export const selectPaymentMethods = createSelector(selectTenancy, tenancy => tenancy.payment_methods);
export const selectTenancyLeases = createSelector(selectTenancy, tenancy => tenancy.tenancy_leases);

export const selectTenancyCois = createSelector(selectTenancy, tenancy => tenancy.tenancy_cois);

export const selectTenancyFloorplans = createSelector(selectTenancy, tenancy => tenancy.tenancy_floorplans);

export const selectTenancyInsuranceRequirementsDocuments = createSelector(
  selectTenancy,
  tenancy => tenancy.tenancy_insurance_requirements_documents
);

export const selectAccessPaths = createSelector(selectTenancy, tenancy => tenancy.access_paths);

export const selectTenancyDocumentsRequestState = createSelector(
  selectTenancy,
  tenancy => tenancy.tenancyDocumentsRequestState
);
